import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { dateTime } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { ArrowsClockwise, CheckCircle, WarningCircle, WarningOctagon } from "@phosphor-icons/react";

const TYPE_LABEL = {
  won_but_unpaid: "Won but unpaid",
  refund_in_window: "Refund in clawback window",
  rate_mismatch: "Rate mismatch",
  activity_lapsed: "Ghost rep — activity lapsed",
  unsigned_contract: "Unsigned contract — commission frozen",
  close_rate_drop: "Close-rate drop",
  stale_opportunity: "Stale opportunity",
  no_show: "No-show",
  payment_refunded: "Votel refund — clawback review",
  payment_disputed: "Votel dispute — clawback review",
};

export default function Reconciliation() {
  const [exceptions, setExceptions] = useState([]);
  const [runs, setRuns] = useState([]);
  const [running, setRunning] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    api.get("/exceptions").then((r) => setExceptions(r.data));
    api.get("/reconciliation-runs").then((r) => setRuns(r.data));
  };
  useEffect(() => { load(); }, []);

  const run = async () => {
    setRunning(true);
    try {
      const r = await api.post("/reconcile");
      const n = r.data.exceptions.length;
      toast.success(n === 0 ? "All reconciled — no exceptions" : `Reconciled — ${n} exception(s)`);
      load();
    } catch (e) { toast.error("Reconciliation failed"); }
    finally { setRunning(false); }
  };

  const confirmFreeze = async (repId) => {
    try { await api.post(`/reps/${repId}/confirm-freeze`); toast.success("Rep frozen — residual accrual stopped"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const resolve = async (e, action) => {
    const reason = window.prompt(action === "clawback" ? "Reason for clawback:" : "Reason to release (pay) this commission:");
    if (!reason || reason.trim().length < 5) { if (reason !== null) toast.error("Reason must be at least 5 characters"); return; }
    try {
      await api.post(`/admin/votel/deals/${e.ref_id}/charges/${e.charge_id}/resolve`, { action, reason });
      toast.success(action === "clawback" ? "Clawback applied" : "Commission released"); load();
    } catch (err) { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="mb-8 flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Reconciliation</h1>
          <p className="mt-1 text-sm text-muted-foreground">Nightly for all reps · idempotent · only exceptions surface</p>
        </div>
        <Button data-testid="reconcile-run-btn" onClick={run} disabled={running} className="gap-2">
          <ArrowsClockwise size={16} weight="bold" className={running ? "animate-spin" : ""} />
          {running ? "Reconciling…" : "Run Reconciliation"}
        </Button>
      </div>

      <h3 className="font-heading mb-3 text-lg font-semibold">Exceptions</h3>
      {exceptions.length === 0 ? (
        <div data-testid="all-reconciled" className="flex items-center gap-3 rounded-sm border border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/10 p-5">
          <CheckCircle size={22} weight="fill" className="text-[hsl(var(--success))]" />
          <div>
            <div className="font-medium text-[hsl(var(--success))]">All reconciled</div>
            <div className="text-sm text-muted-foreground">No exceptions across reps. Clean cycle.</div>
          </div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="exceptions-list">
          {exceptions.map((e) => (
            <div key={e.id} data-testid={`exception-${e.id}`}
              className="flex items-center justify-between rounded-sm border border-border bg-card p-4 transition-colors duration-200 hover:border-muted-foreground/40">
              <div className="flex items-center gap-3">
                {e.severity === "critical"
                  ? <WarningOctagon size={20} weight="fill" className="text-destructive" />
                  : <WarningCircle size={20} weight="fill" className="text-[hsl(var(--warning))]" />}
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{TYPE_LABEL[e.type] || e.type}</span>
                    <StatusBadge status={e.severity} />
                  </div>
                  <div className="text-sm text-muted-foreground">{e.detail}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" className="text-primary" onClick={() => navigate(`/reps/${e.rep_id}`)}>
                  Open {e.rep_name}
                </Button>
                {e.type === "activity_lapsed" && (
                  <Button data-testid={`confirm-freeze-${e.rep_id}`} variant="outline" size="sm" onClick={() => confirmFreeze(e.rep_id)}>Confirm Freeze</Button>
                )}
                {e.charge_id && (
                  <>
                    <Button data-testid={`clawback-${e.charge_id}`} variant="destructive" size="sm" onClick={() => resolve(e, "clawback")}>Apply clawback</Button>
                    <Button data-testid={`release-${e.charge_id}`} variant="outline" size="sm" onClick={() => resolve(e, "release")}>Release</Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <h3 className="font-heading mb-3 mt-8 text-lg font-semibold">Recent Runs</h3>
      <div className="overflow-hidden rounded-sm border border-border">
        <table className="w-full text-sm">
          <thead className="bg-card"><tr className="border-b border-border">
            {["Ran At", "Reps", "Booked", "Reversed", "Exceptions"].map((c, i) => (
              <th key={c} className={`px-4 py-3 overline ${i === 0 ? "text-left" : "text-right"}`}>{c}</th>
            ))}
          </tr></thead>
          <tbody>
            {runs.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-muted-foreground">No runs yet.</td></tr>}
            {runs.map((r) => (
              <tr key={r.id} className="border-b border-border">
                <td className="px-4 py-2.5 font-mono">{dateTime(r.ran_at)}</td>
                <td className="px-4 py-2.5 text-right font-mono">{r.reps_processed}</td>
                <td className="px-4 py-2.5 text-right font-mono text-[hsl(var(--success))]">{r.events_booked}</td>
                <td className="px-4 py-2.5 text-right font-mono text-destructive">{r.events_reversed}</td>
                <td className="px-4 py-2.5 text-right font-mono">{r.exceptions_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
